import { isPossibleMismatch } from './sets/coverage.js';
import { summaryPathsOf } from './sets/summary.js';
import { resultSegments, } from './types.js';
function cell(v) {
    if (v === null || v === undefined)
        return '';
    const s = Array.isArray(v) ? v.join('; ') : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
const row = (cells) => cells.join(',');
const lines = (rows) => rows.join('\r\n') + '\r\n';
function safeFileName(name) {
    return name.replace(/[^A-Za-z0-9_-]/g, '_') || 'table';
}
/** Segment number (1-based) and page range ("21-39", ASCII so spreadsheets read it as text). */
const segmentCells = (s) => [
    String(s.index + 1),
    s.pageStart === s.pageEnd ? String(s.pageStart) : `${s.pageStart}-${s.pageEnd}`,
];
/**
 * One `result.csv` with scalar and flattened group fields, plus one CSV per table field. RFC 4180, CRLF line endings.
 * A result BDA split into several segments (design 4.3 "Long documents") gets `segment` and `pages` columns: one
 * `result.csv` row per segment, and each table row tagged with its segment. Single-segment output is unchanged.
 */
export function toCsvFiles(result) {
    const segments = result.segments && result.segments.length > 1 ? result.segments : null;
    if (!segments)
        return singleSegmentCsv(result);
    const files = [];
    const paths = [...new Set(segments.flatMap((s) => s.fields.map((f) => f.path)))];
    const main = [row(['segment', 'pages', ...paths.map((p) => cell(p))])];
    for (const s of segments) {
        const byPath = new Map(s.fields.map((f) => [f.path, f.value]));
        main.push(row([...segmentCells(s), ...paths.map((p) => cell(byPath.get(p)))]));
    }
    files.push({ name: 'result.csv', content: lines(main) });
    const tableNames = [...new Set(segments.flatMap((s) => s.tables.map((t) => t.name)))];
    for (const name of tableNames) {
        const parts = segments
            .map((s) => ({ s, t: s.tables.find((t) => t.name === name) }))
            .filter((p) => !!p.t);
        const columns = [...new Set(parts.flatMap((p) => p.t.columns))];
        const out = [row(['segment', 'pages', ...columns.map((c) => cell(c))])];
        for (const { s, t } of parts)
            for (const r of t.rows)
                out.push(row([...segmentCells(s), ...columns.map((c) => cell(r[c]?.value))]));
        files.push({ name: `${safeFileName(name)}.csv`, content: lines(out) });
    }
    return files;
}
function singleSegmentCsv(result) {
    const files = [];
    const headers = result.fields.map((f) => cell(f.path));
    const values = result.fields.map((f) => cell(f.value));
    files.push({ name: 'result.csv', content: lines([row(headers), row(values)]) });
    for (const t of result.tables) {
        const out = [row(t.columns.map((c) => cell(c)))];
        for (const r of t.rows)
            out.push(row(t.columns.map((c) => cell(r[c]?.value))));
        files.push({ name: `${safeFileName(t.name)}.csv`, content: lines(out) });
    }
    return files;
}
// ---------- Blueprint sets (blueprint-sets Req 7; design "csv.ts additions") ----------
/** `segments.csv` member value of a No_Match_Segment. */
export const NO_MATCH = 'NO_MATCH';
/** The snapshot Member a Segment matched, or null for a No_Match_Segment. */
const memberOfSegment = (snapshot, s) => s.matchedMember === null || s.matchedMember === undefined ? null : (snapshot.members[s.matchedMember] ?? null);
/** Values in a Segment below the threshold (fields and table cells; a null confidence is not low). */
const lowCount = (s, threshold) => s.fields.filter((f) => f.confidence !== null && f.confidence < threshold).length +
    s.tables.reduce((n, t) => n +
        t.rows.reduce((m, r) => m + Object.values(r).filter((c) => c.confidence !== null && c.confidence < threshold).length, 0), 0);
/** A Possible_Mismatch_Segment at the snapshot's Coverage_Floor that the author did not accept (Req 5.10). */
const isExcludedMismatch = (snapshot, s, accepted) => isPossibleMismatch(s, snapshot.coverageFloor) && !(accepted?.has(s.index) ?? false);
/**
 * Req 7.3-7.5: one `<member>.csv` per Member that matched at least one Segment (one row per Segment: `segment`,
 * `pages`, then every scalar and group path), one `<member>-<table>.csv` per table seen (one row per table row), then
 * `segments.csv` (`segment, pages, member, field_coverage, possible_mismatch, low_confidence`) and `summary.csv`
 * (`kind, name, group, value, status, confidence`). `<member>` is `safeFileName(name)`; every file name is made unique
 * (case-insensitively) by appending `-2`, `-3` in snapshot order. Same `cell()` encoding as `toCsvFiles`, which is
 * unchanged (Req 7.6).
 *
 * Per-Member CSVs leave out Possible_Mismatch_Segments (at the snapshot's `coverageFloor`) that are not in
 * `acceptedSegments`, like the Packet_Summary; `segments.csv` lists every Segment with `field_coverage` as
 * `found/total` (empty when unknown) and `possible_mismatch` `true` only for such an excluded Segment.
 */
export function toSetCsvFiles(args) {
    const { snapshot, summary, threshold, acceptedSegments } = args;
    const segments = resultSegments(args.result);
    const used = new Set(['segments', 'summary']);
    const unique = (base) => {
        let name = base;
        for (let n = 2; used.has(name.toLowerCase()); n++)
            name = `${base}-${n}`;
        used.add(name.toLowerCase());
        return name;
    };
    const files = [];
    snapshot.members.forEach((m, i) => {
        const matched = segments.filter((s) => s.matchedMember === i && !isExcludedMismatch(snapshot, s, acceptedSegments));
        if (matched.length === 0)
            return;
        const base = unique(safeFileName(m.name));
        const schemaPaths = summaryPathsOf(m.schema)
            .filter((p) => !p.table)
            .map((p) => p.path);
        const paths = [...new Set([...schemaPaths, ...matched.flatMap((s) => s.fields.map((f) => f.path))])];
        const main = [row(['segment', 'pages', ...paths.map((p) => cell(p))])];
        for (const s of matched) {
            const byPath = new Map(s.fields.map((f) => [f.path, f.value]));
            main.push(row([...segmentCells(s), ...paths.map((p) => cell(byPath.get(p)))]));
        }
        files.push({ name: `${base}.csv`, content: lines(main) });
        const tableNames = [...new Set(matched.flatMap((s) => s.tables.map((t) => t.name)))];
        for (const tableName of tableNames) {
            const parts = matched
                .map((s) => ({ s, t: s.tables.find((t) => t.name === tableName) }))
                .filter((p) => !!p.t);
            const columns = [...new Set(parts.flatMap((p) => p.t.columns))];
            const out = [row(['segment', 'pages', ...columns.map((c) => cell(c))])];
            for (const { s, t } of parts)
                for (const r of t.rows)
                    out.push(row([...segmentCells(s), ...columns.map((c) => cell(r[c]?.value))]));
            files.push({ name: `${unique(`${base}-${safeFileName(tableName)}`)}.csv`, content: lines(out) });
        }
    });
    const segRows = [row(['segment', 'pages', 'member', 'field_coverage', 'possible_mismatch', 'low_confidence'])];
    for (const s of segments) {
        const m = memberOfSegment(snapshot, s);
        const c = m ? s.fieldCoverage : null;
        segRows.push(row([
            ...segmentCells(s),
            cell(m ? m.name : NO_MATCH),
            c ? `${c.found}/${c.total}` : '',
            String(isExcludedMismatch(snapshot, s, acceptedSegments)),
            String(m ? lowCount(s, threshold) : 0),
        ]));
    }
    files.push({ name: 'segments.csv', content: lines(segRows) });
    const nameOfMember = new Map(snapshot.members.flatMap((m) => (m.blueprintId ? [[m.blueprintId, m.name]] : [])));
    const sum = [row(['kind', 'name', 'group', 'value', 'status', 'confidence'])];
    for (const h of summary.headers)
        sum.push(row([
            'header',
            cell(h.label),
            '',
            cell(h.value),
            h.invalid ? 'INVALID' : h.differs ? 'DIFFERS' : '',
            cell(h.confidence),
        ]));
    for (const r of summary.rollups) {
        sum.push(row(['rollup', cell(r.name), '', cell(r.value), r.invalid ? 'INVALID' : '', '']));
        for (const g of r.groups ?? [])
            sum.push(row(['rollup', cell(r.name), cell(nameOfMember.get(g.group) ?? g.group), cell(g.value), '', '']));
    }
    for (const c of summary.checks)
        sum.push(row(['check', cell(c.name), '', cell(c.difference), c.invalid ? 'INVALID' : c.status, '']));
    sum.push(row(['no_match', 'segments', '', String(summary.noMatchSegments), '', '']));
    sum.push(row(['no_match', 'pages', '', String(summary.noMatchPages), '', '']));
    files.push({ name: 'summary.csv', content: lines(sum) });
    return files;
}
/**
 * Req 7.2: the download JSON of a set run: the set name, its Set_Version (or `DRAFT`), every Segment with its
 * Matched_Member (including BDA's name for it), values, and confidences, and the Packet_Summary. Each Segment also has
 * its `fieldCoverage` and `possibleMismatch` (an excluded Possible_Mismatch_Segment, as in `toSetCsvFiles`), and the
 * top level the snapshot's `coverageFloor`.
 */
export function toSetResultJson(args) {
    const { snapshot, summary, acceptedSegments } = args;
    return {
        set: { setId: snapshot.setId, name: snapshot.setName, version: snapshot.setVersion ?? 'DRAFT' },
        coverageFloor: snapshot.coverageFloor,
        members: snapshot.members.map((m) => ({
            index: m.index,
            blueprintId: m.blueprintId,
            ref: m.ref,
            name: m.name,
            documentType: m.documentType,
            bdaName: m.bdaName,
        })),
        pages: args.result.pages,
        segments: resultSegments(args.result).map((s) => {
            const m = memberOfSegment(snapshot, s);
            return {
                segment: s.index + 1,
                pageStart: s.pageStart,
                pageEnd: s.pageEnd,
                status: s.status,
                member: m
                    ? {
                        index: s.matchedMember,
                        name: m.name,
                        documentType: m.documentType,
                        bdaName: s.matchedBlueprint?.name ?? m.bdaName,
                    }
                    : null,
                matchedBlueprint: s.matchedBlueprint ?? null,
                fieldCoverage: m ? (s.fieldCoverage ?? null) : null,
                possibleMismatch: isExcludedMismatch(snapshot, s, acceptedSegments),
                fields: s.fields,
                tables: s.tables,
            };
        }),
        summary,
    };
}
//# sourceMappingURL=csv.js.map